import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { outfitAdminApi } from '../../../../services/outfitAdminApi';

function Step3FacetsCuration({ selectedCCI, selectedFacets, onChangeFacets, onNext, onBack }) {
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [error, setError] = useState('');

  const cciId = selectedCCI?.category_combination_id;

  const fetchGraph = useCallback(async () => {
    if (!cciId) return;
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchCooccurrenceGraph(cciId);
      if (response && response.data) {
        setGraphData(response.data);
      }
    } catch (err) {
      console.error('Failed to load cooccurrence graph:', err);
      setError(err.message || 'Failed to load facet co-occurrence graph');
    } finally {
      setLoading(false);
    }
  }, [cciId]);

  useEffect(() => {
    fetchGraph();
  }, [fetchGraph]);

  const categories = useMemo(() => graphData?.categories || {}, [graphData]);
  const nodes = useMemo(() => graphData?.nodes || [], [graphData]);
  const cooccurrences = useMemo(() => graphData?.cooccurrences || {}, [graphData]);
  const totalLooksCount = graphData?.total_looks_count || 0;

  // Map nodeId -> node object
  const nodeMap = useMemo(() => {
    const map = new Map();
    nodes.forEach((n) => map.set(n.node_id, n));
    return map;
  }, [nodes]);

  // Map slot -> property -> Array of nodes
  const nodesBySlotAndProp = useMemo(() => {
    const map = {};
    nodes.forEach((node) => {
      if (!map[node.slot]) map[node.slot] = {};
      if (!map[node.slot][node.property]) map[node.slot][node.property] = [];
      map[node.slot][node.property].push(node);
    });
    return map;
  }, [nodes]);

  // Selected node IDs grouped by slot
  const selectedNodeIdsBySlot = useMemo(() => {
    const map = {};
    for (const [slot, propsObj] of Object.entries(selectedFacets)) {
      map[slot] = [];
      for (const [prop, valList] of Object.entries(propsObj)) {
        if (Array.isArray(valList)) {
          valList.forEach((val) => {
            const found = nodes.find(
              (n) => n.slot === slot && n.property === prop && n.value === val
            );
            if (found) {
              map[slot].push(found.node_id);
            }
          });
        }
      }
    }
    return map;
  }, [selectedFacets, nodes]);

  // Check if a candidate node is compatible with selected nodes in OTHER categories
  const isNodeCompatible = useCallback(
    (candidateNodeId, candidateSlot) => {
      for (const [slot, selectedNodeIds] of Object.entries(selectedNodeIdsBySlot)) {
        if (slot === candidateSlot || !selectedNodeIds || selectedNodeIds.length === 0) {
          continue;
        }

        // Candidate node must co-occur with at least one selected node in every other active slot
        const hasValidOverlap = selectedNodeIds.some(
          (selectedId) => (cooccurrences[candidateNodeId]?.[selectedId] ?? 0) > 0
        );

        if (!hasValidOverlap) {
          return false; // Incompatible -> Gray out!
        }
      }
      return true;
    },
    [selectedNodeIdsBySlot, cooccurrences]
  );

  const [matchedPreview, setMatchedPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch live server-side preview of matched count whenever selectedFacets change
  const fetchPreviewCount = useCallback(async (facets) => {
    if (!cciId) return;
    try {
      setPreviewLoading(true);
      const res = await outfitAdminApi.previewMatchedCount({
        category_combination_id: cciId,
        selected_facets: facets,
      });
      if (res && res.data) {
        setMatchedPreview(res.data);
      }
    } catch (err) {
      console.warn('Failed to preview matched count from server:', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [cciId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPreviewCount(selectedFacets);
    }, 150);
    return () => clearTimeout(timer);
  }, [cciId, selectedFacets, fetchPreviewCount]);

  // Compute live matched looks count (client fallback)
  const matchedLooksCount = useMemo(() => {
    if (matchedPreview && typeof matchedPreview.matched_looks_count === 'number') {
      return matchedPreview.matched_looks_count;
    }

    const activeSlots = Object.keys(selectedNodeIdsBySlot).filter(
      (slot) => selectedNodeIdsBySlot[slot].length > 0
    );

    if (activeSlots.length === 0) {
      return totalLooksCount;
    }

    if (activeSlots.length === 1) {
      const slot = activeSlots[0];
      const slotNodeIds = selectedNodeIdsBySlot[slot];
      let count = 0;
      slotNodeIds.forEach((id) => {
        const node = nodeMap.get(id);
        if (node) count = Math.max(count, node.look_count);
      });
      return count;
    }

    // Cross-slot pairs: find minimum overlapping counts
    let minOverlap = totalLooksCount;
    for (let i = 0; i < activeSlots.length; i++) {
      for (let j = i + 1; j < activeSlots.length; j++) {
        const slotA = activeSlots[i];
        const slotB = activeSlots[j];
        let pairOverlap = 0;

        selectedNodeIdsBySlot[slotA].forEach((idA) => {
          selectedNodeIdsBySlot[slotB].forEach((idB) => {
            const count = cooccurrences[idA]?.[idB] || cooccurrences[idB]?.[idA] || 0;
            pairOverlap = Math.max(pairOverlap, count);
          });
        });

        minOverlap = Math.min(minOverlap, pairOverlap);
      }
    }

    return minOverlap;
  }, [matchedPreview, selectedNodeIdsBySlot, totalLooksCount, nodeMap, cooccurrences]);

  // Toggle facet selection
  const handleToggleFacet = (slot, property, value, disabled) => {
    if (disabled) return;

    const currentSlotObj = selectedFacets[slot] || {};
    const currentPropList = currentSlotObj[property] || [];

    let nextPropList;
    if (currentPropList.includes(value)) {
      nextPropList = currentPropList.filter((v) => v !== value);
    } else {
      nextPropList = [...currentPropList, value];
    }

    const nextSlotObj = { ...currentSlotObj };
    if (nextPropList.length > 0) {
      nextSlotObj[property] = nextPropList;
    } else {
      delete nextSlotObj[property];
    }

    const nextFacets = { ...selectedFacets };
    if (Object.keys(nextSlotObj).length > 0) {
      nextFacets[slot] = nextSlotObj;
    } else {
      delete nextFacets[slot];
    }

    onChangeFacets(nextFacets, matchedLooksCount);
  };

  const displayedRatio = matchedPreview
    ? `${matchedPreview.matched_looks_count} / ${matchedPreview.total_looks_count}`
    : `${matchedLooksCount} / ${totalLooksCount}`;

  return (
    <div className="outfit-card">
      <div className="outfit-card-header">
        <div>
          <h2 className="outfit-card-title">Step 3: Curate Visual Facets</h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Select visual attributes. Incompatible facets in other categories will be grayed out in real time.
          </span>
        </div>
      </div>

      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
        </div>
      )}

      {/* Curation Header with Matched Looks Counter */}
      <div className="facets-curation-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: '#475569' }}>
            Active CCI Formula
          </span>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
            {selectedCCI?.combination &&
              Object.entries(selectedCCI.combination)
                .map(([slot, cat]) => `${slot.replace(/_/g, ' ')}: ${Array.isArray(cat) ? cat.join('/') : cat}`)
                .join('  •  ')}
          </span>
        </div>

        <div className="facets-matched-pill" style={{ opacity: previewLoading ? 0.7 : 1 }}>
          Matched Looks: {displayedRatio}
        </div>
      </div>

      {loading ? (
        <div className="outfit-loading-state" style={{ minHeight: '260px' }}>
          <div className="outfit-spinner" />
          <p>Analyzing cross-category co-occurrence matrix...</p>
        </div>
      ) : (
        <div className="facets-columns-container">
          {Object.entries(categories).map(([slotKey, catInfo]) => {
            const slotPropsObj = nodesBySlotAndProp[slotKey] || {};
            const activePropsInSlot = Object.keys(slotPropsObj);

            return (
              <div key={slotKey} className="facet-slot-column">
                <div className="facet-slot-title">
                  <span>{slotKey.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748b' }}>
                    ({catInfo.display_name || catInfo.category_names?.join(', ') || slotKey})
                  </span>
                </div>

                {activePropsInSlot.length === 0 ? (
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    No visual properties registered for this slot.
                  </span>
                ) : (
                  activePropsInSlot.map((propName) => {
                    const propNodes = slotPropsObj[propName] || [];

                    return (
                      <div key={propName} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div className="facet-group-title">{propName}</div>
                        <div className="facet-pills-list">
                          {propNodes.map((node) => {
                            const isSelected =
                              selectedFacets[slotKey]?.[propName]?.includes(node.value) || false;
                            const compatible = isNodeCompatible(node.node_id, slotKey);
                            const disabled = !isSelected && !compatible;

                            return (
                              <div
                                key={node.node_id}
                                className={`facet-pill-item ${isSelected ? 'selected' : ''} ${
                                  disabled ? 'disabled' : ''
                                }`}
                                title={
                                  disabled
                                    ? 'No outfits exist matching this property with your current selections'
                                    : ''
                                }
                                onClick={() =>
                                  handleToggleFacet(slotKey, propName, node.value, disabled)
                                }
                              >
                                <span style={{ fontWeight: isSelected ? '700' : '500' }}>
                                  {node.value}
                                </span>
                                <span className="facet-count">
                                  {disabled ? '0 looks' : `${node.look_count} looks`}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="wizard-nav-bar">
        <button type="button" className="outfit-btn outfit-btn-secondary" onClick={onBack}>
          &larr; Back to CCI Selection
        </button>

        <button
          type="button"
          className="outfit-btn outfit-btn-primary"
          disabled={matchedLooksCount === 0}
          onClick={onNext}
        >
          Next: Review & Publish &rarr;
        </button>
      </div>
    </div>
  );
}

export default Step3FacetsCuration;
